import React from 'react';
import { Link } from 'react-router-dom';
import { Calculator, BookOpen, ArrowRight } from 'lucide-react';
import { miniSimulators, mockArticles } from '../data/mock';
import { articleToSimulators, simulatorToArticles, articleToArticles } from '../data/internalLinks';

const RelatedSimulators = ({ articleSlug }) => {
  const simIds = articleToSimulators[articleSlug] || [];
  const relatedSims = simIds
    .map(id => miniSimulators.find(s => s.id === id))
    .filter(Boolean)
    .slice(0, 3);

  if (relatedSims.length === 0) return null;

  return (
    <div data-testid="related-simulators" className="mt-12 border-t border-gray-200 pt-8">
      <div className="flex items-center gap-2 mb-6">
        <Calculator className="h-5 w-5 text-blue-600" />
        <h3 className="text-xl font-bold text-gray-900">Simulateurs en lien avec cet article</h3>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {relatedSims.map(sim => (
          <Link
            key={sim.id}
            to={`/simulateur/${sim.id}`}
            className="group block bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-100 rounded-xl p-5 hover:shadow-md hover:border-blue-300 transition-all duration-200"
          >
            <span className="text-2xl block mb-2">{sim.icon}</span>
            <h4 className="font-semibold text-gray-900 group-hover:text-blue-700 transition-colors mb-1">{sim.title}</h4>
            <p className="text-sm text-gray-500 mb-3">{sim.description}</p>
            <span className="inline-flex items-center text-sm font-medium text-blue-600 group-hover:text-blue-800">
              Simuler
              <ArrowRight className="h-3.5 w-3.5 ml-1 group-hover:translate-x-1 transition-transform" />
            </span>
          </Link>
        ))}
      </div>
    </div>
  );
};

const RelatedArticlesForArticle = ({ articleSlug }) => {
  const slugs = articleToArticles[articleSlug] || [];
  const relatedArts = slugs
    .map(slug => mockArticles.find(a => a.slug === slug))
    .filter(Boolean)
    .slice(0, 2);

  if (relatedArts.length === 0) return null;

  return (
    <div data-testid="related-articles-from-article" className="mt-8 border-t border-gray-200 pt-8">
      <div className="flex items-center gap-2 mb-6">
        <BookOpen className="h-5 w-5 text-blue-600" />
        <h3 className="text-xl font-bold text-gray-900">Articles connexes</h3>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {relatedArts.map(art => (
          <Link
            key={art.slug}
            to={`/article/${art.slug}`}
            className="group block bg-white border border-gray-200 rounded-xl p-5 hover:shadow-md hover:border-blue-300 transition-all duration-200"
          >
            <span className="text-xs font-medium text-blue-600 uppercase tracking-wide">{art.category}</span>
            <h4 className="font-semibold text-gray-900 group-hover:text-blue-700 transition-colors mt-1 mb-2 leading-snug">{art.title}</h4>
            <p className="text-sm text-gray-500 line-clamp-2">{art.excerpt}</p>
            <span className="inline-flex items-center text-sm font-medium text-blue-600 mt-3 group-hover:text-blue-800">
              Lire l'article
              <ArrowRight className="h-3.5 w-3.5 ml-1 group-hover:translate-x-1 transition-transform" />
            </span>
          </Link>
        ))}
      </div>
    </div>
  );
};

const RelatedArticlesForSimulator = ({ simulatorId }) => {
  const slugs = simulatorToArticles[simulatorId] || [];
  const relatedArts = slugs
    .map(slug => mockArticles.find(a => a.slug === slug))
    .filter(Boolean)
    .slice(0, 3);

  if (relatedArts.length === 0) return null;

  return (
    <div data-testid="related-articles-from-simulator" className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
      <div className="border-t border-gray-200 pt-8">
        <div className="flex items-center gap-2 mb-6">
          <BookOpen className="h-5 w-5 text-blue-600" />
          <h3 className="text-xl font-bold text-gray-900">Articles pour aller plus loin</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {relatedArts.map(art => (
            <Link
              key={art.slug}
              to={`/article/${art.slug}`}
              className="group block bg-white border border-gray-200 rounded-xl p-5 hover:shadow-md hover:border-blue-300 transition-all duration-200"
            >
              <span className="text-xs font-medium text-blue-600 uppercase tracking-wide">{art.category}</span>
              <h4 className="font-semibold text-gray-900 group-hover:text-blue-700 transition-colors mt-1 mb-2 leading-snug text-sm">{art.title}</h4>
              <p className="text-xs text-gray-500 line-clamp-2">{art.excerpt}</p>
              <span className="inline-flex items-center text-sm font-medium text-blue-600 mt-3 group-hover:text-blue-800">
                Lire
                <ArrowRight className="h-3.5 w-3.5 ml-1 group-hover:translate-x-1 transition-transform" />
              </span>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

export { RelatedSimulators, RelatedArticlesForArticle, RelatedArticlesForSimulator };
